import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background, Controls, MiniMap, addEdge, useNodesState, useEdgesState,
  Handle, Position, MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  Phone, Plus, Trash2, Save, ArrowLeft, PlayCircle, MessageSquare, ListOrdered,
  Clock, Users, User, PhoneForwarded, Voicemail, Hash,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { voiceAPI } from '@/lib/api';

const uid = (p) => `${p}_${Math.random().toString(36).slice(2, 9)}`;

const DOW = [['Mon', 0], ['Tue', 1], ['Wed', 2], ['Thu', 3], ['Fri', 4], ['Sat', 5], ['Sun', 6]];

const NODE_META = {
  entry: { label: 'Call starts', icon: PlayCircle, color: '#0f2547' },
  message: { label: 'Message', icon: MessageSquare, color: '#0891b2' },
  menu: { label: 'IVR menu', icon: ListOrdered, color: '#7c3aed' },
  hours: { label: 'Business hours', icon: Clock, color: '#b45309' },
  ring_group: { label: 'Ring a team', icon: Users, color: '#059669' },
  ring_agent: { label: 'Ring an agent', icon: User, color: '#059669' },
  forward: { label: 'Forward to number', icon: PhoneForwarded, color: '#dc2626' },
  voicemail: { label: 'Voicemail', icon: Voicemail, color: '#475569' },
};

const NODE_DEFAULTS = {
  message: { text_en: 'Thank you for calling.', text_es: 'Gracias por llamar.' },
  menu: {
    bilingual: true,
    lang_prompt: 'For English press 1. Para espanol oprima 2.',
    greeting_en: 'For sales press 1. To leave a message press 9.',
    greeting_es: 'Para ventas oprima 1. Para dejar un mensaje oprima 9.',
    options: [{ digit: '1', label: 'Sales' }, { digit: '9', label: 'Voicemail' }],
  },
  hours: { mode: 'business', timezone: 'America/New_York', days: [0, 1, 2, 3, 4], start: '09:00', end: '18:00' },
  ring_group: { group_id: '', timeout: 20 },
  ring_agent: { user_id: '', timeout: 20 },
  forward: { number: '', timeout: 30 },
  voicemail: { greeting_en: '', greeting_es: '' },
};

// ── Custom node renderer ──────────────────────────────────────────
const handleStyle = { width: 9, height: 9, background: '#94a3b8' };

function FlowNode({ id, type, data, selected }) {
  const meta = NODE_META[type] || NODE_META.message;
  const Icon = meta.icon;
  const outHandles = [];
  if (type === 'entry' || type === 'message') outHandles.push({ id: null, label: '' });
  else if (type === 'hours') { outHandles.push({ id: 'open', label: 'Open' }, { id: 'closed', label: 'Closed' }); }
  else if (type === 'menu') {
    (data.options || []).forEach((o) => outHandles.push({ id: String(o.digit), label: `${o.digit} · ${o.label || ''}` }));
    outHandles.push({ id: 'timeout', label: 'No input' });
  } else if (['ring_group', 'ring_agent', 'forward'].includes(type)) outHandles.push({ id: 'no_answer', label: 'No answer' });

  return (
    <div
      data-testid={`flownode-${type}`}
      style={{ borderColor: selected ? meta.color : '#e2e8f0' }}
      className="rounded-xl border-2 bg-white shadow-sm min-w-[190px]"
    >
      {type !== 'entry' && <Handle type="target" position={Position.Top} style={handleStyle} />}
      <div className="flex items-center gap-2 px-3 py-2 rounded-t-xl text-white text-xs font-semibold" style={{ background: meta.color }}>
        <Icon size={14} /> {meta.label}
      </div>
      <div className="px-3 py-2 text-[11px] text-slate-600 space-y-1">
        {type === 'message' && <div className="line-clamp-2">{data.text_en || '—'}</div>}
        {type === 'menu' && <div>{(data.options || []).length} option(s){data.bilingual ? ' · bilingual' : ''}</div>}
        {type === 'hours' && <div>{data.mode === 'custom' ? `Custom ${data.start}–${data.end}` : 'Follows business hours'}</div>}
        {type === 'ring_group' && <div>{data._group_name || 'Choose a team…'}</div>}
        {type === 'ring_agent' && <div>{data._agent_name || 'Choose an agent…'}</div>}
        {type === 'forward' && <div>{data.number || 'External number…'}</div>}
        {type === 'voicemail' && <div>Leave a message</div>}
        {type === 'entry' && <div>Entry point</div>}
      </div>
      <div className="relative pb-3">
        {outHandles.map((h, i) => (
          <div key={h.id || `out${i}`} className="flex items-center justify-end pr-4 h-5 text-[10px] text-slate-400">
            <span>{h.label}</span>
            <Handle
              type="source"
              position={Position.Bottom}
              id={h.id || undefined}
              style={{ ...handleStyle, background: meta.color, position: 'relative', transform: 'none', left: 6, top: 0 }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

const nodeTypes = {
  entry: FlowNode, message: FlowNode, menu: FlowNode, hours: FlowNode,
  ring_group: FlowNode, ring_agent: FlowNode, forward: FlowNode, voicemail: FlowNode,
};

// ── Editor ────────────────────────────────────────────────────────
function FlowEditor({ flow, numbers, groups, agents, onBack, onSaved }) {
  const [name, setName] = useState(flow.name || 'Untitled flow');
  const [enabled, setEnabled] = useState(!!flow.enabled);
  const [assigned, setAssigned] = useState(flow.numbers || []);
  const [nodes, setNodes, onNodesChange] = useNodesState(
    (flow.nodes && flow.nodes.length)
      ? flow.nodes
      : [{ id: 'n_entry', type: 'entry', position: { x: 260, y: 40 }, data: {} }],
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState(flow.edges || []);
  const [selId, setSelId] = useState(null);
  const [saving, setSaving] = useState(false);

  const groupName = useCallback((gid) => (groups.find((g) => g.group_id === gid) || {}).name, [groups]);
  const agentName = useCallback((aid) => {
    const a = agents.find((x) => (x.user_id || x.id) === aid);
    return a ? (a.name || a.display_name || a.email) : '';
  }, [agents]);

  const onConnect = useCallback((params) => {
    setEdges((eds) => {
      // one outgoing edge per (source, handle)
      const cleaned = eds.filter((e) => !(e.source === params.source && (e.sourceHandle || null) === (params.sourceHandle || null)));
      return addEdge({ ...params, markerEnd: { type: MarkerType.ArrowClosed } }, cleaned);
    });
  }, [setEdges]);

  const addNode = (type) => {
    const id = uid('nd');
    setNodes((nds) => nds.concat({
      id, type,
      position: { x: 120 + Math.random() * 260, y: 200 + Math.random() * 220 },
      data: JSON.parse(JSON.stringify(NODE_DEFAULTS[type] || {})),
    }));
    setSelId(id);
  };

  const patchData = (patch) => {
    setNodes((nds) => nds.map((n) => (n.id === selId ? { ...n, data: { ...n.data, ...patch } } : n)));
  };

  const deleteSelected = () => {
    if (!selId) return;
    const node = nodes.find((n) => n.id === selId);
    if (node?.type === 'entry') { toast.error('The entry node cannot be deleted'); return; }
    setNodes((nds) => nds.filter((n) => n.id !== selId));
    setEdges((eds) => eds.filter((e) => e.source !== selId && e.target !== selId));
    setSelId(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      const cleanNodes = nodes.map((n) => ({
        id: n.id, type: n.type, position: n.position,
        data: Object.fromEntries(Object.entries(n.data || {}).filter(([k]) => !k.startsWith('_'))),
      }));
      const cleanEdges = edges.map((e) => ({ id: e.id, source: e.source, target: e.target, sourceHandle: e.sourceHandle || null }));
      const payload = { name, enabled, numbers: assigned, nodes: cleanNodes, edges: cleanEdges };
      const res = flow.flow_id
        ? await voiceAPI.updateFlow(flow.flow_id, payload)
        : await voiceAPI.createFlow(payload);
      toast.success('Call flow saved');
      onSaved(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save the flow');
    } finally {
      setSaving(false);
    }
  };

  // decorate ring nodes with resolved names for the card preview
  const decorated = useMemo(() => nodes.map((n) => {
    if (n.type === 'ring_group') return { ...n, data: { ...n.data, _group_name: groupName(n.data.group_id) } };
    if (n.type === 'ring_agent') return { ...n, data: { ...n.data, _agent_name: agentName(n.data.user_id) } };
    return n;
  }), [nodes, groupName, agentName]);

  const sel = nodes.find((n) => n.id === selId);

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      <div className="flex items-center gap-3 px-4 py-3 border-b bg-white">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="flow-back-btn"><ArrowLeft size={16} /></Button>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs font-semibold" data-testid="flow-name-input" />
        <div className="flex items-center gap-2">
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="flow-enabled-switch" />
          <span className="text-sm text-slate-600">{enabled ? 'Live' : 'Draft'}</span>
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" onClick={save} disabled={saving} className="bg-[#0f2547]" data-testid="flow-save-btn"><Save size={15} className="mr-1" />Save</Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Palette */}
        <div className="w-44 border-r bg-slate-50 p-3 space-y-2 overflow-y-auto">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Add step</div>
          {['message', 'menu', 'hours', 'ring_group', 'ring_agent', 'forward', 'voicemail'].map((t) => {
            const M = NODE_META[t]; const Ic = M.icon;
            return (
              <button key={t} onClick={() => addNode(t)} data-testid={`flow-add-${t}`}
                className="w-full flex items-center gap-2 text-xs font-medium text-slate-700 bg-white border border-slate-200 rounded-lg px-2 py-2 hover:border-slate-400 transition-colors">
                <Ic size={14} style={{ color: M.color }} /> {M.label}
              </button>
            );
          })}
          <div className="pt-3 mt-3 border-t">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-1">Phone numbers</div>
            {numbers.length === 0 && <div className="text-[11px] text-slate-400">No numbers found</div>}
            {numbers.map((n) => (
              <label key={n.number} className="flex items-start gap-2 text-[11px] text-slate-700 py-1 cursor-pointer">
                <input type="checkbox" className="mt-0.5 accent-[#0f2547]"
                  checked={assigned.includes(n.number)}
                  onChange={(e) => setAssigned((a) => (e.target.checked ? [...a, n.number] : a.filter((x) => x !== n.number)))}
                  data-testid={`flow-number-${n.number}`} />
                <span>{n.number}{n.nickname ? ` · ${n.nickname}` : ''}{n.dedicated ? ' (agent)' : ''}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Canvas */}
        <div className="flex-1 min-w-0">
          <ReactFlow
            nodes={decorated}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            onNodeClick={(_, n) => setSelId(n.id)}
            onPaneClick={() => setSelId(null)}
            fitView
            deleteKeyCode={null}
          >
            <Background gap={16} color="#e2e8f0" />
            <Controls />
            <MiniMap zoomable pannable />
          </ReactFlow>
        </div>

        {/* Inspector */}
        {sel && (
          <div className="w-80 border-l bg-white p-4 overflow-y-auto" data-testid="flow-inspector">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-slate-800">{NODE_META[sel.type]?.label}</div>
              {sel.type !== 'entry' && (
                <Button variant="ghost" size="sm" onClick={deleteSelected} data-testid="flow-delete-node"><Trash2 size={15} className="text-rose-600" /></Button>
              )}
            </div>
            <NodeInspector node={sel} patch={patchData} groups={groups} agents={agents} />
          </div>
        )}
      </div>
    </div>
  );
}

function NodeInspector({ node, patch, groups, agents }) {
  const d = node.data || {};
  const t = node.type;
  if (t === 'entry') return <p className="text-xs text-slate-500">This is where every call to this flow begins. Connect it to the first step.</p>;

  if (t === 'message') return (
    <div className="space-y-3">
      <div><Label className="text-xs">Message (English)</Label><Textarea rows={3} value={d.text_en || ''} onChange={(e) => patch({ text_en: e.target.value })} data-testid="ins-msg-en" /></div>
      <div><Label className="text-xs">Message (Español)</Label><Textarea rows={3} value={d.text_es || ''} onChange={(e) => patch({ text_es: e.target.value })} data-testid="ins-msg-es" /></div>
    </div>
  );

  if (t === 'menu') return (
    <div className="space-y-3">
      <label className="flex items-center gap-2 text-xs"><Switch checked={!!d.bilingual} onCheckedChange={(v) => patch({ bilingual: v })} data-testid="ins-menu-bilingual" /> Ask language first (EN/ES)</label>
      {d.bilingual && <div><Label className="text-xs">Language prompt</Label><Textarea rows={2} value={d.lang_prompt || ''} onChange={(e) => patch({ lang_prompt: e.target.value })} /></div>}
      <div><Label className="text-xs">Greeting (English)</Label><Textarea rows={2} value={d.greeting_en || ''} onChange={(e) => patch({ greeting_en: e.target.value })} data-testid="ins-menu-greet-en" /></div>
      <div><Label className="text-xs">Greeting (Español)</Label><Textarea rows={2} value={d.greeting_es || ''} onChange={(e) => patch({ greeting_es: e.target.value })} /></div>
      <div>
        <Label className="text-xs">Keypad options</Label>
        <div className="space-y-1.5 mt-1">
          {(d.options || []).map((o, i) => (
            <div key={i} className="flex items-center gap-1.5">
              <div className="flex items-center gap-1 border rounded px-1.5 py-1"><Hash size={11} className="text-slate-400" />
                <input value={o.digit} onChange={(e) => { const opts = [...d.options]; opts[i] = { ...o, digit: e.target.value.replace(/[^0-9*#]/g, '').slice(0, 1) }; patch({ options: opts }); }} className="w-6 text-sm outline-none" data-testid={`ins-menu-digit-${i}`} /></div>
              <Input value={o.label} onChange={(e) => { const opts = [...d.options]; opts[i] = { ...o, label: e.target.value }; patch({ options: opts }); }} placeholder="Label" className="h-8 text-sm" />
              <Button variant="ghost" size="sm" onClick={() => patch({ options: d.options.filter((_, j) => j !== i) })}><Trash2 size={13} className="text-rose-500" /></Button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="w-full" onClick={() => patch({ options: [...(d.options || []), { digit: '', label: '' }] })} data-testid="ins-menu-add-option"><Plus size={13} className="mr-1" />Add option</Button>
        </div>
        <p className="text-[10px] text-slate-400 mt-1">Connect each option's dot to the next step. "No input" catches timeouts.</p>
      </div>
    </div>
  );

  if (t === 'hours') return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs">Schedule source</Label>
        <select value={d.mode || 'business'} onChange={(e) => patch({ mode: e.target.value })} className="w-full mt-1 text-sm border rounded-lg py-1.5 px-2" data-testid="ins-hours-mode">
          <option value="business">Follow global business hours</option>
          <option value="custom">Custom schedule (this flow)</option>
        </select>
      </div>
      {d.mode === 'custom' && (
        <>
          <div>
            <Label className="text-xs">Timezone</Label>
            <select value={d.timezone || 'America/New_York'} onChange={(e) => patch({ timezone: e.target.value })} className="w-full mt-1 text-sm border rounded-lg py-1.5 px-2">
              {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles', 'America/Phoenix'].map((z) => <option key={z} value={z}>{z}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1"><Label className="text-xs">Open</Label><Input type="time" value={d.start || '09:00'} onChange={(e) => patch({ start: e.target.value })} className="h-8" /></div>
            <div className="flex-1"><Label className="text-xs">Close</Label><Input type="time" value={d.end || '18:00'} onChange={(e) => patch({ end: e.target.value })} className="h-8" /></div>
          </div>
          <div>
            <Label className="text-xs">Open days</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {DOW.map(([lbl, idx]) => {
                const on = (d.days || []).includes(idx);
                return <button key={idx} onClick={() => patch({ days: on ? d.days.filter((x) => x !== idx) : [...(d.days || []), idx] })} className={`text-[11px] rounded-full px-2 py-1 border ${on ? 'bg-[#0f2547] text-white border-[#0f2547]' : 'bg-white text-slate-600 border-slate-200'}`}>{lbl}</button>;
              })}
            </div>
          </div>
        </>
      )}
      <p className="text-[10px] text-slate-400">Connect the "Open" and "Closed" dots to different steps.</p>
    </div>
  );

  if (t === 'ring_group') return (
    <div className="space-y-3">
      <div><Label className="text-xs">Team</Label>
        <select value={d.group_id || ''} onChange={(e) => patch({ group_id: e.target.value })} className="w-full mt-1 text-sm border rounded-lg py-1.5 px-2" data-testid="ins-ring-group">
          <option value="">Choose a team…</option>
          {groups.map((g) => <option key={g.group_id} value={g.group_id}>{g.name}</option>)}
        </select>
      </div>
      <div><Label className="text-xs">Ring for (seconds)</Label><Input type="number" value={d.timeout || 20} onChange={(e) => patch({ timeout: Number(e.target.value) })} className="h-8" /></div>
      <p className="text-[10px] text-slate-400">Connect "No answer" to a fallback (e.g. voicemail).</p>
    </div>
  );

  if (t === 'ring_agent') return (
    <div className="space-y-3">
      <div><Label className="text-xs">Agent</Label>
        <select value={d.user_id || ''} onChange={(e) => patch({ user_id: e.target.value })} className="w-full mt-1 text-sm border rounded-lg py-1.5 px-2" data-testid="ins-ring-agent">
          <option value="">Choose an agent…</option>
          {agents.map((a) => <option key={a.user_id || a.id} value={a.user_id || a.id}>{a.name || a.display_name || a.email}</option>)}
        </select>
      </div>
      <div><Label className="text-xs">Ring for (seconds)</Label><Input type="number" value={d.timeout || 20} onChange={(e) => patch({ timeout: Number(e.target.value) })} className="h-8" /></div>
      <p className="text-[10px] text-slate-400">Connect "No answer" to a fallback.</p>
    </div>
  );

  if (t === 'forward') return (
    <div className="space-y-3">
      <div><Label className="text-xs">External number (E.164)</Label><Input value={d.number || ''} onChange={(e) => patch({ number: e.target.value })} placeholder="+18135550123" className="h-8" data-testid="ins-forward-number" /></div>
      <div><Label className="text-xs">Ring for (seconds)</Label><Input type="number" value={d.timeout || 30} onChange={(e) => patch({ timeout: Number(e.target.value) })} className="h-8" /></div>
    </div>
  );

  if (t === 'voicemail') return (
    <div className="space-y-3">
      <div><Label className="text-xs">Custom greeting (English) — optional</Label><Textarea rows={2} value={d.greeting_en || ''} onChange={(e) => patch({ greeting_en: e.target.value })} /></div>
      <div><Label className="text-xs">Custom greeting (Español) — optional</Label><Textarea rows={2} value={d.greeting_es || ''} onChange={(e) => patch({ greeting_es: e.target.value })} /></div>
      <p className="text-[10px] text-slate-400">Leave blank to use the default voicemail greeting.</p>
    </div>
  );
  return null;
}

// ── List + page shell ─────────────────────────────────────────────
export default function CallFlowsPage() {
  const [flows, setFlows] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [agents, setAgents] = useState([]);
  const [editing, setEditing] = useState(null); // flow object or {new:true}
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, n, s, a] = await Promise.all([
        voiceAPI.listFlows(),
        voiceAPI.flowNumbers(),
        voiceAPI.settings(),
        voiceAPI.assignableAgents().catch(() => ({ data: { agents: [] } })),
      ]);
      setFlows(f.data.items || []);
      setNumbers(n.data.numbers || []);
      setGroups(s.data.ring_groups || []);
      setAgents(a.data.agents || a.data.items || (Array.isArray(a.data) ? a.data : []));
    } catch (e) {
      toast.error('Could not load call flows');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const remove = async (id) => {
    if (!window.confirm('Delete this call flow?')) return;
    try { await voiceAPI.deleteFlow(id); toast.success('Flow deleted'); load(); }
    catch { toast.error('Could not delete'); }
  };

  if (editing) {
    return (
      <FlowEditor
        flow={editing.new ? { name: 'New flow', enabled: false, numbers: [], nodes: [], edges: [] } : editing}
        numbers={numbers} groups={groups} agents={agents}
        onBack={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="call-flows-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Phone size={22} /> Call Flows</h1>
          <p className="text-sm text-slate-500 mt-1">Design how each phone number routes incoming calls — visually, like a call-flow canvas.</p>
        </div>
        <Button onClick={() => setEditing({ new: true })} className="bg-[#0f2547]" data-testid="flow-create-btn"><Plus size={16} className="mr-1" />New flow</Button>
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : flows.length === 0 ? (
        <div className="border-2 border-dashed rounded-xl p-12 text-center text-slate-500" data-testid="flow-empty">
          <Phone size={32} className="mx-auto mb-3 text-slate-300" />
          <p className="font-medium">No call flows yet</p>
          <p className="text-sm mt-1">Create one and assign it to a phone number. Numbers without a flow keep today's behavior.</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {flows.map((f) => (
            <div key={f.flow_id} className="flex items-center gap-4 bg-white border rounded-xl p-4 hover:shadow-sm transition-shadow" data-testid={`flow-card-${f.flow_id}`}>
              <div className={`h-2.5 w-2.5 rounded-full ${f.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800">{f.name}</div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {(f.numbers || []).length ? f.numbers.join(', ') : 'No numbers assigned'} · {(f.nodes || []).length} steps
                </div>
              </div>
              <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${f.enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{f.enabled ? 'Live' : 'Draft'}</span>
              <Button variant="outline" size="sm" onClick={() => setEditing(f)} data-testid={`flow-edit-${f.flow_id}`}>Edit</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(f.flow_id)} data-testid={`flow-remove-${f.flow_id}`}><Trash2 size={15} className="text-rose-500" /></Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
